export interface ValidationRule {
  id: string;
  type: 'compare_dates' | 'compare_numbers' | 'min_length' | 'max_length' | 'custom_pattern' | 'dependent_value';
  compareField?: string;
  compareOperator?: 'greater_than' | 'less_than' | 'greater_equal' | 'less_equal' | 'equal' | 'not_equal';
  value?: string | number;
  message: string;
  dependentValue?: string;
}

export interface FieldValidation {
  min?: number;
  max?: number;
  pattern?: string;
  custom?: {
    enabled: boolean;
    rules: ValidationRule[];
  };
}

export interface ValidationError {
  fieldId: string;
  fieldName: string;
  message: string;
  rule?: ValidationRule;
}

/**
 * Validates a single field against its validation rules
 */
export const validateField = (
  fieldId: string,
  fieldName: string,
  fieldType: string,
  fieldValue: any,
  validation: FieldValidation | undefined,
  allFieldValues: Record<string, any>,
  allFields: Array<{ id: string; name: string; type: string; label: string }>
): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!validation) return errors;

  // Basic validation - only run if field has a value
  if (fieldValue !== undefined && fieldValue !== null && String(fieldValue).trim() !== '') {
    if (fieldType === 'text' || fieldType === 'textarea' || fieldType === 'email') {
      const stringValue = String(fieldValue || '');
      
      if (validation.min !== undefined && stringValue.length < validation.min) {
        errors.push({
          fieldId,
          fieldName,
          message: `Minimum length is ${validation.min} characters`
        });
      }
      
      if (validation.max !== undefined && stringValue.length > validation.max) {
        errors.push({
          fieldId,
          fieldName,
          message: `Maximum length is ${validation.max} characters`
        });
      }
      
      if (validation.pattern && stringValue) {
        try {
          const regex = new RegExp(validation.pattern);
          if (!regex.test(stringValue)) {
            errors.push({
              fieldId,
              fieldName,
              message: 'Invalid format'
            });
          }
        } catch (e) {
          console.error('Invalid regex pattern:', validation.pattern);
        }
      }
    }

    if (fieldType === 'number') {
      const numValue = parseFloat(fieldValue);
      
      if (!isNaN(numValue)) {
        if (validation.min !== undefined && numValue < validation.min) {
          errors.push({
            fieldId,
            fieldName,
            message: `Minimum value is ${validation.min}`
          });
        }
        
        if (validation.max !== undefined && numValue > validation.max) {
          errors.push({
            fieldId,
            fieldName,
            message: `Maximum value is ${validation.max}`
          });
        }
      }
    }
  }

  // Custom validation rules
  if (validation.custom?.enabled && validation.custom.rules) {
    for (const rule of validation.custom.rules) {
      const error = validateCustomRule(
        fieldId,
        fieldName,
        fieldType,
        fieldValue,
        rule,
        allFieldValues,
        allFields
      );
      if (error) {
        errors.push(error);
      }
    }
  }

  return errors;
};

/**
 * Validates a custom validation rule
 */
const validateCustomRule = (
  fieldId: string,
  fieldName: string,
  fieldType: string,
  fieldValue: any,
  rule: ValidationRule,
  allFieldValues: Record<string, any>,
  allFields: Array<{ id: string; name: string; type: string; label: string }>
): ValidationError | null => {
  switch (rule.type) {
    case 'compare_dates':
      return validateDateComparison(fieldId, fieldName, fieldValue, rule, allFieldValues, allFields);
    
    case 'compare_numbers':
      return validateNumberComparison(fieldId, fieldName, fieldValue, rule, allFieldValues, allFields);
    
    case 'min_length':
      // Only check if field has a value
      if (fieldValue !== undefined && fieldValue !== null) {
        const minString = String(fieldValue || '');
        if (minString.length < (rule.value as number || 0)) {
          return {
            fieldId,
            fieldName,
            message: rule.message,
            rule
          };
        }
      }
      break;
    
    case 'max_length':
      // Only check if field has a value
      if (fieldValue !== undefined && fieldValue !== null) {
        const maxString = String(fieldValue || '');
        if (maxString.length > (rule.value as number || 0)) {
          return {
            fieldId,
            fieldName,
            message: rule.message,
            rule
          };
        }
      }
      break;
    
    case 'custom_pattern':
      // Only check if field has a value
      if (fieldValue !== undefined && fieldValue !== null) {
        const patternString = String(fieldValue || '');
        if (patternString && rule.value) {
          try {
            const regex = new RegExp(rule.value as string);
            if (!regex.test(patternString)) {
              return {
                fieldId,
                fieldName,
                message: rule.message,
                rule
              };
            }
          } catch (e) {
            console.error('Invalid regex pattern in custom rule:', rule.value);
          }
        }
      }
      break;
    
    case 'dependent_value':
      // Always check dependent value validation regardless of whether current field has value
      return validateDependentValue(fieldId, fieldName, fieldValue, rule, allFieldValues, allFields);
  }

  return null;
};

/**
 * Validates date comparison between two fields
 */
const validateDateComparison = (
  fieldId: string,
  fieldName: string,
  fieldValue: any,
  rule: ValidationRule,
  allFieldValues: Record<string, any>,
  allFields: Array<{ id: string; name: string; type: string; label: string }>
): ValidationError | null => {
  if (!rule.compareField || !rule.compareOperator) return null;

  const compareField = allFields.find(f => f.id === rule.compareField);
  if (!compareField) return null;

  const currentDate = new Date(fieldValue);
  const compareDate = new Date(allFieldValues[compareField.name]);

  // Skip validation if either date is invalid
  if (isNaN(currentDate.getTime()) || isNaN(compareDate.getTime())) {
    return null;
  }

  let isValid = true;
  
  switch (rule.compareOperator) {
    case 'greater_than':
      isValid = currentDate > compareDate;
      break;
    case 'greater_equal':
      isValid = currentDate >= compareDate;
      break;
    case 'less_than':
      isValid = currentDate < compareDate;
      break;
    case 'less_equal':
      isValid = currentDate <= compareDate;
      break;
    case 'equal':
      isValid = currentDate.getTime() === compareDate.getTime();
      break;
    case 'not_equal':
      isValid = currentDate.getTime() !== compareDate.getTime();
      break;
  }

  if (!isValid) {
    return {
      fieldId,
      fieldName,
      message: rule.message,
      rule
    };
  }

  return null;
};

/**
 * Validates number comparison between two fields
 */
const validateNumberComparison = (
  fieldId: string,
  fieldName: string,
  fieldValue: any,
  rule: ValidationRule,
  allFieldValues: Record<string, any>,
  allFields: Array<{ id: string; name: string; type: string; label: string }>
): ValidationError | null => {
  if (!rule.compareField || !rule.compareOperator) return null;

  const compareField = allFields.find(f => f.id === rule.compareField);
  if (!compareField) return null;

  const currentNumber = parseFloat(fieldValue);
  const compareNumber = parseFloat(allFieldValues[compareField.name]);

  // Skip validation if either value is not a valid number
  if (isNaN(currentNumber) || isNaN(compareNumber)) {
    return null;
  }

  let isValid = true;
  
  switch (rule.compareOperator) {
    case 'greater_than':
      isValid = currentNumber > compareNumber;
      break;
    case 'greater_equal':
      isValid = currentNumber >= compareNumber;
      break;
    case 'less_than':
      isValid = currentNumber < compareNumber;
      break;
    case 'less_equal':
      isValid = currentNumber <= compareNumber;
      break;
    case 'equal':
      isValid = currentNumber === compareNumber;
      break;
    case 'not_equal':
      isValid = currentNumber !== compareNumber;
      break;
  }

  if (!isValid) {
    return {
      fieldId,
      fieldName,
      message: rule.message,
      rule
    };
  }

  return null;
};

/**
 * Validates dependent value rules
 */
const validateDependentValue = (
  fieldId: string,
  fieldName: string,
  fieldValue: any,
  rule: ValidationRule,
  allFieldValues: Record<string, any>,
  allFields: Array<{ id: string; name: string; type: string; label: string }>
): ValidationError | null => {
  if (!rule.compareField || !rule.dependentValue) return null;

  const compareField = allFields.find(f => f.id === rule.compareField);
  if (!compareField) return null;

  const dependentFieldValue = allFieldValues[compareField.name];
  
  // Check if the dependent field has the trigger value
  if (String(dependentFieldValue) === String(rule.dependentValue)) {
    // The dependent field has the trigger value, so this field must have the required value
    const currentValue = String(fieldValue || '').trim();
    const requiredValue = String(rule.value || '').trim();
    
    if (requiredValue !== '') {
      // Specific value required
      if (currentValue !== requiredValue) {
        return {
          fieldId,
          fieldName,
          message: rule.message,
          rule
        };
      }
    } else {
      // Any non-empty value required
      if (currentValue === '') {
        return {
          fieldId,
          fieldName,
          message: rule.message,
          rule
        };
      }
    }
  }

  return null;
};

/**
 * Validates all fields in a form
 */
export const validateForm = (
  formFields: Array<{
    id: string;
    name: string;
    type: string;
    label: string;
    required: boolean;
    validation?: FieldValidation;
  }>,
  formValues: Record<string, any>
): ValidationError[] => {
  const allErrors: ValidationError[] = [];

  for (const field of formFields) {
    // Skip validation for non-input fields
    if (['heading', 'paragraph', 'divider', 'html'].includes(field.type)) {
      continue;
    }

    const fieldValue = formValues[field.name];

    // Check required fields
    if (field.required && (!fieldValue || String(fieldValue).trim() === '')) {
      allErrors.push({
        fieldId: field.id,
        fieldName: field.name,
        message: `${field.label} is required`
      });
      continue; // Skip other validations if field is required but empty
    }

    // Run field-specific validations
    const fieldErrors = validateField(
      field.id,
      field.name,
      field.type,
      fieldValue,
      field.validation,
      formValues,
      formFields
    );
    allErrors.push(...fieldErrors);
  }

  return allErrors;
};

/**
 * Groups validation errors by field for easier display
 */
export const groupErrorsByField = (errors: ValidationError[]): Record<string, ValidationError[]> => {
  return errors.reduce((acc, error) => {
    if (!acc[error.fieldName]) {
      acc[error.fieldName] = [];
    }
    acc[error.fieldName].push(error);
    return acc;
  }, {} as Record<string, ValidationError[]>);
};
